import type { Variation } from "schemas/generated/variation";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import {
  analyticsLinkRouter,
  analyticsLinkUtils,
} from "@v2/trpc/routes/analyticsLink";
import { experimentRouter, experimentUtils } from "@v2/trpc/routes/experiment";
import { v4 as uuid } from "uuid";

/**
 * Simple way to get create a default slug that is probably unique,
 * and reasonable to the user.
 */
export function positiveIntToCapitalLetter(i: number): string {
  return String.fromCharCode(96 + Math.min(Math.max(i, 1), 26));
}

export function generateExperimentVariation({
  slug,
  target,
  allocationPercent = 50,
}: {
  slug: string;
  target: string | null;
  allocationPercent?: number;
}): Variation {
  return {
    id: uuid(),
    slug,
    target,
    allocationPercent,
  };
}

export const NO_LINK_TEXT = "(no link)";

/**
 * Provides all experiment API interactions as a single hook, allowing us to more easily refetch
 * the list and achieve "cache-like" behavior across uses of the query while avoiding indirection.
 *
 * TODO (Max, 2024-10-23, REPL-14267): Move this to @editor/utils/experiments.ts when cleaning up (
 * and delete other functions in this file).
 *
 * @author Ben 2024-04-26
 */
export function useExperimentApi() {
  const workspaceId = useCurrentWorkspaceId() ?? undefined;
  const projectId = useCurrentProjectId();

  const list = experimentRouter.list.useQuery(
    workspaceId || projectId
      ? {
          workspaceId,
          projectId,
        }
      : skipToken,
  );
  const links = analyticsLinkRouter.list.useQuery(
    workspaceId || projectId
      ? {
          workspaceId,
          projectId,
        }
      : skipToken,
  );
  const onSuccess = async () => {
    void experimentUtils.list.invalidate({
      workspaceId,
      projectId,
    });
    void analyticsLinkUtils.list.invalidate({
      workspaceId,
      projectId,
    });
  };
  const create = experimentRouter.create.useMutation({
    onSuccess,
  });
  const update = experimentRouter.update.useMutation({
    onSuccess,
  });
  const remove = experimentRouter.remove.useMutation({
    onSuccess,
  });
  const complete = experimentRouter.complete.useMutation({
    onSuccess,
  });
  return { list, links, update, remove, create, complete };
}
